import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { RGB } from '../../types';
import { getNearestColorName, rgbToHex, hexToRgb, rgbToHsl, hslToRgb, rgbToCmyk, rgbToOklch } from '../../utils/colors';
import { generateTints, generateShades } from '../../utils/colorAnalysis';
import { updateColorDetailMetadata } from '../../utils/seo';
import ColorExplorerHeader from './ColorExplorerHeader';
import ColorUsage from '../../components/ColorUsage';
import ColorReferenceContent from '../../components/ColorReferenceContent';
import Breadcrumb from '../../components/Breadcrumb';
import { getColorReference } from '../../data/colorCatalog';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import ColorIdentityCard from '../../components/ColorIdentityCard';
import TintsAndShades from '../../components/TintsAndShades';
import AccessibilitySection from '../../components/AccessibilitySection';
import WebsitePreview from '../../components/WebsitePreview';
import ColorBlindnessSimulator from '../../components/ColorBlindnessSimulator';
import ImageColorExtractor from '../../components/ImageColorExtractor';
import ColorWheel from '../../components/ColorWheel';
import CodeExportModal from '../../components/CodeExportModal';
import AdBanner from '../../components/AdBanner';
import CollapsibleSection from '../../components/CollapsibleSection';
import ThemesEntryCard from '../../components/ThemesEntryCard';
import { Copy, X, SwatchBook, Layers, Monitor, Accessibility, Image } from '../../components/icons';
import type { ExportColorEntry } from '../../utils/codeExport';

const AD_SLOT_IN_ARTICLE = '8843219021';
const AD_SLOT_BOTTOM = '5842618766';

const getRandomColor = (): RGB => {
  const h = Math.floor(Math.random() * 360);
  const s = Math.floor(Math.random() * 41) + 60;
  const l = Math.floor(Math.random() * 36) + 50;
  return hslToRgb(h, s, l);
};

const isValidHex = (s: string) => /^[0-9A-Fa-f]{6}$/.test(s);

const ColorExplorerPage: React.FC = () => {
  const { hex } = useParams<{ hex?: string }>();
  const navigate = useNavigate();

  const [color, setColor] = useState<RGB>(() => {
    if (hex && isValidHex(hex)) {
      return hexToRgb('#' + hex) || { r: 59, g: 130, b: 246 };
    }
    return { r: 59, g: 130, b: 246 };
  });

  const [harmonyColors, setHarmonyColors] = useState<RGB[]>([]);
  const [colorHistory, setColorHistory] = useState<string[]>([]);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedDisplayHex, setSelectedDisplayHex] = useState<string | null>(null);

  const colorName = useMemo(() => getNearestColorName(color.r, color.g, color.b), [color]);
  const colorHex = useMemo(() => rgbToHex(color.r, color.g, color.b), [color]);

  // SEO metadata
  useEffect(() => {
    updateColorDetailMetadata(colorHex, colorName, color);
  }, [colorHex, colorName, color, hex]);

  // URL sync
  useEffect(() => {
    const timer = setTimeout(() => {
      const cleanHex = colorHex.replace('#', '');
      const newPath = `/color/${cleanHex}`;
      if (window.location.pathname !== newPath) {
        navigate(newPath, { replace: true });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [colorHex, navigate]);

  // When URL hex changes externally
  useEffect(() => {
    if (hex && isValidHex(hex)) {
      const newRgb = hexToRgb('#' + hex);
      if (newRgb && (newRgb.r !== color.r || newRgb.g !== color.g || newRgb.b !== color.b)) {
        addToHistory();
        setColor(newRgb);
      }
    }
  }, [hex]);

  const addToHistory = useCallback(() => {
    const currentHex = rgbToHex(color.r, color.g, color.b).replace('#', '');
    setColorHistory((prev) => {
      const filtered = prev.filter((h) => h !== currentHex);
      const next = [currentHex, ...filtered];
      return next.slice(0, 20);
    });
  }, [color]);

  const handleColorChange = useCallback((newColor: RGB) => {
    setColor(newColor);
    setSelectedDisplayHex(null);
  }, []);

  const handleHarmonyChange = useCallback((colors: RGB[]) => {
    setHarmonyColors(colors);
  }, []);

  const handleRandomize = useCallback(() => {
    addToHistory();
    setColor(getRandomColor());
  }, [addToHistory]);

  const handleShare = useCallback(() => {
    const cleanHex = colorHex.replace('#', '');
    navigator.clipboard.writeText(`${window.location.origin}/color/${cleanHex}`);
    setShowShareTooltip(true);
    setTimeout(() => setShowShareTooltip(false), 2000);
  }, [colorHex]);

  const handleHistorySelect = useCallback((histHex: string) => {
    const rgb = hexToRgb('#' + histHex);
    if (rgb) {
      addToHistory();
      setColor(rgb);
    }
  }, [addToHistory]);

  const exportColors = useMemo((): ExportColorEntry[] => {
    const hsl = rgbToHsl(color.r, color.g, color.b);
    const cmyk = rgbToCmyk(color.r, color.g, color.b);
    const oklch = rgbToOklch(color.r, color.g, color.b);
    return [{
      name: colorName.toLowerCase().replace(/\s+/g, '-'),
      hex: colorHex,
      tints: generateTints(colorHex, 9),
      shades: generateShades(colorHex, 9),
      hsl,
      cmyk,
      oklch,
    }];
  }, [color, colorName, colorHex]);

  return (
    <Layout maxWidth="max-w-6xl">
      <Breadcrumb items={[{ label: 'Color names', to: '/colors' }, { label: colorName }]} />
      <PageHeader icon={SwatchBook} title={`${colorName} Color — ${colorHex} HEX Code`} description={`${getColorReference(colorHex) ? `${colorName} uses ${colorHex}, or RGB(${color.r}, ${color.g}, ${color.b}), in this color catalog.` : `${colorHex} is RGB(${color.r}, ${color.g}, ${color.b}); ${colorName} is its nearest reference name.`} Explore complementary colors, palettes and text contrast below.`} />
      <ColorExplorerHeader
        color={color}
        colorHistory={colorHistory}
        showShareTooltip={showShareTooltip}
        onShare={handleShare}
        onExport={() => setShowExportModal(true)}
        onRandomize={handleRandomize}
        onHistorySelect={handleHistorySelect}
      />

      <div>

        <div className="max-w-6xl mx-auto flex flex-col gap-4 md:gap-6 pb-6">
          {/* Color Wheel — primary interaction */}
          <ColorWheel
            baseColor={color}
            onBaseColorChange={handleColorChange}
            onHarmonyChange={handleHarmonyChange}
            colorName={colorName}
          />

          {/* Color Identity Card */}
          <ColorIdentityCard color={color} />
          <ColorReferenceContent hex={colorHex} rgb={color} />
          <ColorUsage hex={colorHex} />

          {/* Tints & Shades */}
          <CollapsibleSection title="Shades & Tints" icon={<Layers size={20} aria-hidden="true" />}>
            {selectedDisplayHex && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-surface-sunken rounded-xl">
                <div
                  className="w-10 h-10 rounded-lg border border-surface-border shadow-inner"
                  style={{ backgroundColor: selectedDisplayHex }}
                />
                <span className="font-mono text-sm font-bold text-ink">
                  {selectedDisplayHex.toUpperCase()}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(selectedDisplayHex);
                  }}
                  className="p-1.5 text-ink-muted hover:text-brand-600 transition-colors"
                  title="Copy hex"
                >
                  <Copy size={14} />
                </button>
                <button
                  onClick={() => setSelectedDisplayHex(null)}
                  className="ml-auto p-1.5 text-ink-muted hover:text-ink transition-colors"
                  title="Deselect"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            <TintsAndShades
              color={color}
              onColorSelect={setSelectedDisplayHex}
              selectedHex={selectedDisplayHex}
            />
          </CollapsibleSection>

          {/* Theme Palettes entry */}
          <ThemesEntryCard color={color} />

          {/* Website Preview — uses harmony colors */}
          <CollapsibleSection title="Live Website Preview" icon={<Monitor size={20} aria-hidden="true" />}>
            <WebsitePreview harmonyColors={harmonyColors} />
          </CollapsibleSection>

          {/* Accessibility + Color Blindness */}
          <CollapsibleSection title="Accessibility" icon={<Accessibility size={20} aria-hidden="true" />}>
            <div className="grid md:grid-cols-2 gap-4">
              <AccessibilitySection color={color} />
              <ColorBlindnessSimulator
                colors={[{ hex: colorHex, label: colorName }]}
              />
            </div>
          </CollapsibleSection>

          <AdBanner slotId={AD_SLOT_IN_ARTICLE} format="auto" />

          {/* Image Extractor */}
          <CollapsibleSection title="Extract Colors from Image" icon={<Image size={20} aria-hidden="true" />}>
            <ImageColorExtractor />
          </CollapsibleSection>

          <AdBanner slotId={AD_SLOT_BOTTOM} format="horizontal" />


        </div>
      </div>

      <CodeExportModal
        colors={exportColors}
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </Layout>
  );
};

export default ColorExplorerPage;
